<template>
  <label
    :class="[
      'q-toggle',
      `q-toggle--${size}`,
      {
        'q-toggle--disabled': disabled,
        'q-toggle--hover': hover,
        'q-toggle--selected': modelValue,
      },
    ]"
    :style="{ opacity: disabled ? 0.25 : 1 }"
  >
    <input
      type="checkbox"
      :checked="modelValue"
      :disabled="disabled"
      class="q-toggle__input"
      @change="$emit('update:modelValue', $event.target.checked)"
      @click="$emit('click', $event)"
    />

    <div class="q-toggle__track">
      <div class="q-toggle__thumb"></div>
    </div>

    <span class="q-toggle__text">
      <slot />
    </span>
  </label>
</template>

<script setup>
defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'l',
    validator: (value) => ['l', 'm', 's'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hover: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['update:modelValue', 'click']);
</script>

<style lang="scss" scoped>
.q-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__track {
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 60000px;
    background-color: rgba(14, 14, 34, 0.1);
    transition: all 0.2s ease;
    overflow: hidden;

    .q-toggle--l & {
      width: 60px;
      height: 36px;
      padding: 4px;
    }

    .q-toggle--m & {
      width: 42px;
      height: 24px;
      padding: 3px;
    }

    .q-toggle--s & {
      width: 32px;
      height: 20px;
      padding: 2px;
    }
  }

  &__thumb {
    border-radius: 50%;
    background-color: var(--color-white);
    transition: all 0.2s ease;
    flex-shrink: 0;

    .q-toggle--l & {
      width: 28px;
      height: 28px;
    }

    .q-toggle--m & {
      width: 18px;
      height: 18px;
    }

    .q-toggle--s & {
      width: 16px;
      height: 16px;
    }
  }

  &__text {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 20px;
    line-height: 1.2;
    color: rgba(14, 14, 34, 0.8);

    .q-toggle--s & {
      font-size: 14px;
    }
  }

  // Selected state
  &--selected {
    .q-toggle__track {
      background-color: #6ca1f8;
      justify-content: flex-end;
    }

    .q-toggle__text {
      color: rgba(14, 14, 34, 0.8);
    }
  }

  // Hover states
  &:hover:not(.q-toggle--disabled),
  &--hover:not(.q-toggle--disabled) {
    .q-toggle__track {
      background-color: rgba(14, 14, 34, 0.2);
    }

    .q-toggle__text {
      color: rgba(14, 14, 34, 0.9);
    }

    &.q-toggle--selected {
      .q-toggle__track {
        background-color: #3b82f6;
      }
    }
  }

  // Disabled state
  &--disabled {
    cursor: not-allowed;

    .q-toggle__track {
      background-color: rgba(14, 14, 34, 0.1);
    }

    .q-toggle__text {
      color: rgba(14, 14, 34, 0.8);
    }
  }
}
</style>
